// Twenty-odd independent AFL models, weighted by how well each one has been
// doing, and nobody has published whether the blend adds anything to the price.
//
// Squiggle (api.squiggle.com.au) collects the weekly tips of every public AFL
// model — each one a confidence out of 100 that the home side wins — and gives
// them away free, with no key, asking only for a contact address in the
// User-Agent. That is a crowd of forecasters built by people with no money on
// the game and no reason to agree with each other, over a market no overseas
// syndicate is pushing. The evidence says an ensemble like that beats almost
// every member of it, because the errors are not the same errors.
//
// WHERE THE TIPS COME FROM, and it is never the phone. `server/cron/features/
// squiggle.mjs` fetches the round once a day and writes `features/afl/{date}.
// json`; `scripts/ingest-squiggle-tips.mjs` attaches the same bag to the stored
// history so a backtest can be run over seasons. Either way the tips arrive on
// the match as `match.features.squiggle.tips`, and this model reads that and
// nothing else — no network, no clock, no storage, like every other model here.
//
// PUNTERS IS NOT A MODEL (`sourceid` 5). It is the market itself wearing a
// tipster's hat — Punters' own consensus is derived from bookmaker prices — so
// including it would let the board vote on whether the board is wrong, and the
// encompassing test would then be asking the market about itself. It is dropped
// at the fetch, at the ingest and again here, because three places that must
// agree are cheaper than one place everybody forgets.
//
// THE WEIGHTS: each source carries its own walk-forward log loss over the prior
// `window` games, and weight falls off exponentially in that loss —
//
//     w_s ∝ exp(−eta · (L_s − L̄))
//
// which is the multiplicative-weights rule (Hedge) over experts, with `eta`
// deciding how hard a good run is rewarded. At the default 10 a source half a
// point of log loss better than the field carries about 1.65 times the weight
// of an average one, which is a tilt rather than a winner-take-all. A source
// with fewer than `minGames` settled games is given the field's own mean loss,
// so a model that joined last month is neither punished nor promoted on four
// results.
//
// `L_s` is an exponentially weighted mean with one match's decay per match
// scored, so the effective memory is the last `window` games and a model that
// was good in 2021 and poor since is read as poor. It is walk-forward because
// it is only ever accumulated forward: `fit` folds the history up to `asOf`,
// `update` folds one finished match in at the end of a day, and neither can see
// a result it is about to be asked to predict.
//
// AND IT STILL GOES THROUGH THE GATE. The crowd being a good forecaster is not
// the question; whether it knows something the price does not is
// (`encompassing.js`), and under t = 1.96 on a walk-forward sample Model crowd
// prints nothing at all.

import { assertBefore } from './base.js';

/**
 * Squiggle's own id for "Punters", which is the market derived from bookmaker
 * prices rather than a model of the football. Excluded everywhere.
 */
export const PUNTERS_SOURCE_ID = 5;

/** Source ids that never count as a forecaster. One, and the reason is above. */
export const EXCLUDED_SOURCES = [PUNTERS_SOURCE_ID];

// A model that says 100 is still not certain, and a probability of exactly 1
// is useless to everything downstream — it makes log loss infinite and the
// edge calculation meaningless.
const FLOOR = 0.001;

const clamp = (p) => Math.min(Math.max(p, FLOOR), 1 - FLOOR);

/** The prior games a source's log loss is measured over. One AFL season, near enough. */
export const WINDOW = 200;

/** How hard a good run is rewarded. See the Hedge note above. */
export const ETA = 10;

/** Below this many settled games a source's own loss is noise, so the field's is used. */
export const MIN_GAMES = 30;

const DEFAULT_PARAMS = { window: WINDOW, eta: ETA, minGames: MIN_GAMES };

/**
 * One tip as a feature row, or null where it is not one.
 *
 * `hconfidence` is the confidence the HOME side wins. Squiggle sends it
 * directly on most rows; where it is missing, `confidence` belongs to whoever
 * `tip` names, so an away tip has to be flipped before it can sit beside the
 * rest. `homeId` is what says which of them that was — pass it where the caller
 * knows, and a row with neither reading is dropped rather than guessed.
 *
 * `margin` is Squiggle's own field and belongs to the side that source TIPPED,
 * not to the home side. It is carried because it is free and somebody will want
 * it; nothing in the app reads it, and anything that starts to must flip it the
 * way `hconfidence` is flipped.
 */
export function featureTip(tip, { homeId = null, teamIdOf = null } = {}) {
  const sourceid = Number(tip?.sourceid);
  if (!Number.isFinite(sourceid) || EXCLUDED_SOURCES.includes(sourceid)) return null;

  let hconfidence = Number(tip?.hconfidence);
  if (!Number.isFinite(hconfidence)) {
    const confidence = Number(tip?.confidence);
    if (!Number.isFinite(confidence) || !homeId || !teamIdOf) return null;
    hconfidence = teamIdOf(tip?.tip) === homeId ? confidence : 100 - confidence;
  }
  if (hconfidence < 0 || hconfidence > 100) return null;

  const margin = Number(tip?.margin);
  return {
    sourceid,
    source: tip?.source ?? String(sourceid),
    hconfidence,
    margin: Number.isFinite(margin) ? margin : null,
    updated: tip?.updated ?? null,
  };
}

/**
 * The bag that rides on a match: what Squiggle's models said about it, and the
 * two ids that let somebody check it against the feed a year later.
 *
 * Sorted by source id so that two runs of the same ingest write the same bytes
 * and a re-ingest is a no-op rather than a diff.
 */
export function squiggleFeature(tips, { gameid = null, round = null, homeId = null, teamIdOf = null } = {}) {
  const rows = [];
  const seen = new Set();
  for (const tip of tips ?? []) {
    const row = featureTip(tip, { homeId, teamIdOf });
    if (!row || seen.has(row.sourceid)) continue;
    seen.add(row.sourceid);
    rows.push(row);
  }
  if (!rows.length) return null;
  rows.sort((a, b) => a.sourceid - b.sourceid);
  return { gameid, round, tips: rows };
}

/** The tips on a match, whichever shape it arrived in, or an empty list. */
export function tipsOf(match) {
  const tips = match?.features?.squiggle?.tips;
  return Array.isArray(tips) ? tips.filter((t) => Number.isFinite(Number(t?.hconfidence))) : [];
}

/** Mean of the confidences: the flat ensemble, which is what the weights are judged against. */
export function ensembleP(hconfidence) {
  const values = (hconfidence ?? []).filter((v) => Number.isFinite(v));
  if (values.length === 0) return null;
  return clamp(values.reduce((sum, v) => sum + v, 0) / values.length / 100);
}

/** Cross-entropy of one forecast against one 0/1 outcome. */
const loss = (p, y) => -(y * Math.log(clamp(p)) + (1 - y) * Math.log(1 - clamp(p)));

/** Did this match finish with a side in front? A draw teaches a two-way model nothing. */
function homeWin(match) {
  const result = match?.result;
  if (!result || result.status !== 'final') return null;
  if (result.winner === 'home') return 1;
  if (result.winner === 'away') return 0;
  return null;
}

/**
 * One finished match folded into the running per-source losses.
 *
 * Every source is decayed, not only the ones that tipped this match: evidence
 * ages with the calendar, and a model that stopped publishing in 2023 should
 * fade rather than keep the weight its last good season earned it.
 */
export function accumulate(state, match, params = DEFAULT_PARAMS) {
  const y = homeWin(match);
  const tips = tipsOf(match);
  if (y === null || !tips.length) return state ?? null;

  const p = { ...DEFAULT_PARAMS, ...(state?.params ?? {}), ...params };
  const decay = 1 - 1 / Math.max(2, p.window);
  const sources = {};
  for (const [id, row] of Object.entries(state?.sources ?? {})) {
    sources[id] = { ...row, loss: row.loss * decay, w: row.w * decay };
  }
  for (const tip of tips) {
    const id = String(tip.sourceid);
    const row = sources[id] ?? { source: tip.source ?? id, loss: 0, w: 0, n: 0 };
    sources[id] = {
      source: tip.source ?? row.source,
      loss: row.loss + loss(tip.hconfidence / 100, y),
      w: row.w + 1,
      n: row.n + 1,
    };
  }
  return {
    sources,
    n: (state?.n ?? 0) + 1,
    params: p,
    fittedTo: new Date(match.kickoff).toISOString(),
  };
}

/**
 * The weight each of these sources carries, normalised to sum to one.
 *
 * Sources are weighted against each OTHER, on this match, so a round where half
 * the field did not publish is still a proper weighted mean rather than one
 * that quietly sums to 0.4. A source nobody has enough games on takes the
 * field's mean loss, which is the weight of an average model.
 */
export function weightsFor(state, sourceids) {
  const p = { ...DEFAULT_PARAMS, ...(state?.params ?? {}) };
  const ids = [...new Set((sourceids ?? []).map((id) => String(id)))];
  if (!ids.length) return {};

  const losses = {};
  const measured = [];
  for (const id of ids) {
    const row = state?.sources?.[id];
    const mean = row && row.w > 0 && row.n >= p.minGames ? row.loss / row.w : null;
    losses[id] = mean;
    if (mean !== null) measured.push(mean);
  }
  const mid = measured.length ? measured.reduce((a, b) => a + b, 0) / measured.length : 0;

  const raw = {};
  let total = 0;
  for (const id of ids) {
    const l = losses[id] ?? mid;
    const w = Math.exp(-p.eta * (l - mid));
    raw[id] = Number.isFinite(w) && w > 0 ? w : 0;
    total += raw[id];
  }
  if (!(total > 0)) {
    const flat = 1 / ids.length;
    return Object.fromEntries(ids.map((id) => [id, flat]));
  }
  return Object.fromEntries(ids.map((id) => [id, raw[id] / total]));
}

export const squiggleEnsemble = {
  id: 'squiggle-ensemble',
  version: 2,
  // AFL and nothing else: Squiggle is an AFL project, and there is no crowd of
  // public models to blend in any other code this app covers.
  sports: ['afl'],
  // The tips are a FEATURE, not the fit; what is fitted is how much each
  // tipster's vote is worth, which takes results and therefore takes history.
  needsFit: true,
  defaultParams: { ...DEFAULT_PARAMS },

  /**
   * Walk the history in kickoff order and fold every finished match with tips on
   * it into the per-source losses. `strict` refuses a training set that contains
   * a match it is about to predict, as every fit here does.
   */
  fit(history, asOf, params = {}, { strict = false } = {}) {
    const p = { ...DEFAULT_PARAMS, ...params };
    const matches = [...assertBefore(history, asOf, strict)]
      .sort((a, b) => String(a?.kickoff).localeCompare(String(b?.kickoff)));

    let state = { sources: {}, n: 0, params: p, fittedTo: new Date(asOf).toISOString() };
    let withTips = 0;
    for (const match of matches) {
      if (tipsOf(match).length) withTips += 1;
      state = accumulate(state, match, p) ?? state;
    }
    return {
      ...state,
      matches: withTips,
      params: p,
      fittedTo: new Date(asOf).toISOString(),
    };
  },

  /** The nightly fold: one finished match, the same arithmetic as the fit's. */
  update(state, match) {
    return accumulate(state, match) ?? state;
  },

  /**
   * The weighted crowd's probability for one side, or null where this match
   * carries no tips — which is every match before the features job ran, every
   * sport that is not the AFL, and any board nobody has joined the day's file
   * onto. Null rather than a guess: a model with nothing to say says nothing.
   */
  predict(state, match, selection) {
    if (selection !== 'home' && selection !== 'away') return null;
    const tips = tipsOf(match);
    if (!tips.length) return null;
    const weights = weightsFor(state, tips.map((t) => t.sourceid));
    let pHome = 0;
    let total = 0;
    for (const tip of tips) {
      const w = weights[String(tip.sourceid)] ?? 0;
      pHome += w * (Number(tip.hconfidence) / 100);
      total += w;
    }
    if (!(total > 0)) return null;
    const p = clamp(pHome / total);
    return selection === 'home' ? p : 1 - p;
  },
};
